// Reading and writing RiftDeck's "Export Txt" format.
//
//   Legend:
//   1 Kennen, Heart of the Tempest
//
//   Champion:
//   1 Kennen, Storm of Shuriken
//
//   MainDeck:
//   3 Fizz, Trickster
//   ...
//
//   Battlefields:
//   1 Forbidding Waste
//
//   Rune Pool:
//   9 Chaos Rune
//
//   Sideboard:
//   1 Salvage
//
// Cards are named, not numbered, which is the whole difficulty: a name has to
// be resolved against the card database, several printings share one name, and
// a deck that silently drops the two cards it could not match is worse than one
// that refuses to import. Every unmatched line is reported.

import type { DeckList } from '../rules/engine.ts';
import type { CardDef } from '../rules/types.ts';
import type { CardIndex } from '../rules/cards.ts';

export interface ImportResult {
  deck: DeckList | null;
  /** Lines that named no card, or named one that cannot go in that section. */
  problems: string[];
  /** Cards in the Sideboard section, kept but not part of the deck. */
  sideboard: string[];
}

/** Section headings, lowercased and stripped of spaces and punctuation. */
const SECTIONS: Record<string, keyof Sections> = {
  legend: 'legend',
  champion: 'champion',
  champions: 'champion',
  maindeck: 'main',
  main: 'main',
  deck: 'main',
  battlefield: 'battlefields',
  battlefields: 'battlefields',
  runepool: 'runes',
  runes: 'runes',
  rune: 'runes',
  sideboard: 'sideboard',
};

interface Sections {
  legend: string[];
  champion: string[];
  main: string[];
  battlefields: string[];
  runes: string[];
  sideboard: string[];
}

/** Names differ only by punctuation and case between tools; this is the key. */
const normalise = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Finds the card a written name refers to. */
export function resolveName(
  written: string, names: Map<string, CardDef>,
): CardDef | undefined {
  return names.get(normalise(written));
}

/**
 * Builds a name lookup over the card database.
 *
 * Two things make this more than a map of names.
 *
 * Several printings share a name -- alternate arts, signed versions -- and any
 * of them plays identically, so the first is as good as another. It is sorted
 * so the choice is stable rather than dependent on database order: two imports
 * of the same text must produce the same deck.
 *
 * And **Riot's gallery names a legend without its champion**: the card called
 * "Heart of the Tempest" is written by RiftDeck as "Kennen, Heart of the
 * Tempest". So every card is also registered under each of its tags prefixed
 * to its name, which matches that spelling with or without the comma, since
 * punctuation is normalised away. A composite key never displaces a real name:
 * if some card is genuinely called "Kennen Heart of the Tempest", that wins.
 */
export function nameIndex(index: CardIndex): Map<string, CardDef> {
  const byName = new Map<string, CardDef[]>();
  for (const card of index.all()) {
    const key = normalise(card.name);
    const list = byName.get(key);
    if (list) list.push(card);
    else byName.set(key, [card]);
  }

  const out = new Map<string, CardDef>();
  const pick = (list: CardDef[]): CardDef =>
    [...list].sort((a, b) => a.id.localeCompare(b.id))[0]!;
  for (const [key, list] of byName) out.set(key, pick(list));

  // Second pass, so the composite keys can see which plain names are taken.
  for (const [key, list] of byName) {
    const card = pick(list);
    for (const tag of card.tags) {
      const composite = normalise(tag) + key;
      if (!out.has(composite)) out.set(composite, card);
    }
  }
  return out;
}

/** Parses one "3 Card Name" line. Returns null for blanks and headings. */
function parseLine(line: string): { count: number; name: string } | null {
  const m = /^\s*(\d+)\s*[xX]?\s+(.+?)\s*$/.exec(line);
  if (!m) return null;
  const count = Number(m[1]);
  if (!Number.isFinite(count) || count < 1 || count > 60) return null;
  return { count, name: m[2]! };
}

export function importDeck(
  text: string, index: CardIndex, names = nameIndex(index),
): ImportResult {
  const sections: Sections = {
    legend: [], champion: [], main: [], battlefields: [], runes: [], sideboard: [],
  };
  const problems: string[] = [];
  let current: keyof Sections | null = null;

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    // A heading ends in a colon, which is what separates it from a card line.
    if (line.endsWith(':')) {
      const key = SECTIONS[normalise(line.slice(0, -1))];
      if (key) { current = key; continue; }
      problems.push(`unknown section "${line}"`);
      current = null;
      continue;
    }

    const entry = parseLine(line);
    if (!entry) { problems.push(`could not read "${line}"`); continue; }
    if (!current) { problems.push(`"${entry.name}" is not under any section`); continue; }

    const card = resolveName(entry.name, names);
    if (!card) { problems.push(`no card named "${entry.name}"`); continue; }
    for (let i = 0; i < entry.count; i++) sections[current].push(card.id);
  }

  // 103. The deck's shape. Reported rather than repaired: a deck quietly padded
  // to legality is not the deck the player exported.
  const legend = sections.legend[0];
  const champion = sections.champion[0];
  const battlefield = sections.battlefields[0];
  if (!legend) problems.push('no legend');
  if (!champion) problems.push('no chosen champion');
  if (!battlefield) problems.push('no battlefield');

  for (const [what, ids] of [
    ['legend', sections.legend], ['champion', sections.champion],
  ] as const) {
    if (ids.length > 1) problems.push(`${ids.length} cards in the ${what} section, expected 1`);
  }

  if (!legend || !champion || !battlefield) {
    return { deck: null, problems, sideboard: sections.sideboard };
  }

  // 108.3.d. The Chosen Champion is played from its own zone, and RiftDeck
  // lists it separately -- but the engine expects it present in the main deck
  // as well, which is where `buildDeck` puts it.
  const main = sections.main.includes(champion)
    ? sections.main
    : [champion, ...sections.main];

  return {
    deck: {
      name: index.get(legend).name,
      legend,
      chosenChampion: champion,
      battlefield,
      main,
      runes: sections.runes,
    },
    problems,
    sideboard: sections.sideboard,
  };
}

/** Writes a deck back out in the same format, so a round trip is possible. */
export function exportDeck(deck: DeckList, index: CardIndex): string {
  const tally = (ids: readonly string[]): string[] => {
    const counts = new Map<string, number>();
    for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
    return [...counts]
      .map(([id, n]) => `${n} ${index.get(id).name}`)
      .sort((a, b) => a.slice(a.indexOf(' ')).localeCompare(b.slice(b.indexOf(' '))));
  };

  const section = (title: string, lines: string[]): string =>
    (lines.length > 0 ? `${title}:\n${lines.join('\n')}\n` : '');

  // The champion is listed in its own section, so it is not repeated in the
  // main deck -- which is how RiftDeck writes it, and what its own importer
  // expects to read back.
  const mainWithoutChampion = [...deck.main];
  const at = mainWithoutChampion.indexOf(deck.chosenChampion);
  if (at >= 0) mainWithoutChampion.splice(at, 1);

  return [
    section('Legend', tally([deck.legend])),
    section('Champion', tally([deck.chosenChampion])),
    section('MainDeck', tally(mainWithoutChampion)),
    section('Battlefields', tally([deck.battlefield])),
    section('Rune Pool', tally(deck.runes)),
  ].filter(Boolean).join('\n');
}
